/**
 * What the panels share around the hand: the magnetic tilt a card leans with,
 * drops onto places the verbs table has no verb for (a card's holes, the
 * Director's prompt) by pointer, click and keyboard alike, the one test for
 * "a carry is under way", redraws that keep keyboard focus, and whether a
 * verb already covers a drop. The Deck, the Account screen, the Gallery, the
 * Director, the MCP lens and the stage all use these, so none of them needs
 * another screen to get them.
 */

import type { CarryState, Gestures, Held, Target, Unsub, Verb } from '../contracts';
import { reducedMotion } from './a11y';

/** Magnetic tilt: at most `max` degrees toward the pointer, and where the sheen sits (percent). */
export const tiltOf = (x: number, y: number, w: number, hgt: number, max = 6): { rx: number; ry: number; mx: number; my: number } => {
  const px = w > 0 ? Math.min(1, Math.max(0, x / w)) : 0.5;
  const py = hgt > 0 ? Math.min(1, Math.max(0, y / hgt)) : 0.5;
  const round = (n: number): number => Math.round(n * 100) / 100;
  return { rx: round((px - 0.5) * 2 * max), ry: round(-(py - 0.5) * 2 * max), mx: round(px * 100), my: round(py * 100) };
};

/** Give a card the magnetic tilt and the pointer-following sheen (through the CSSOM: the CSP has no inline styles). */
export const magnetic = (el: HTMLElement): Unsub => {
  const move = (e: PointerEvent): void => {
    if (e.pointerType === 'touch') return;
    const r = el.getBoundingClientRect();
    const t = tiltOf(e.clientX - r.left, e.clientY - r.top, r.width, r.height, reducedMotion(el.ownerDocument) ? 0 : 6);
    el.style.setProperty('--mx', `${t.mx}%`);
    el.style.setProperty('--my', `${t.my}%`);
    el.style.setProperty('--rx', `${t.rx}deg`);
    el.style.setProperty('--ry', `${t.ry}deg`);
    el.classList.add('pg-hot');
  };
  const leave = (): void => {
    for (const p of ['--mx', '--my', '--rx', '--ry']) el.style.removeProperty(p);
    el.classList.remove('pg-hot');
  };
  el.classList.add('pg-card');
  el.addEventListener('pointermove', move);
  el.addEventListener('pointerleave', leave);
  return () => {
    el.removeEventListener('pointermove', move);
    el.removeEventListener('pointerleave', leave);
    leave();
  };
};

/**
 * One road per drop. A click that drops (below) acts itself and then puts the carry down with
 * `cancel()`; the idle that cancel makes is not a second drop. Every watcher skips a carry a
 * click has already dropped: by the state object, and, for hands that tell their watchers
 * while `cancel()` is still running, by the moment.
 */
const droppedByClick = new WeakSet<object>();
let clickDropping = 0;

/**
 * Drops on targets the verbs table has no verb for (a card's holes, the Director's prompt):
 * watched on the Carry machine itself. A carry that ends idle while over one of them, and
 * was not cancelled with Esc, is a drop there, unless a click already dropped it.
 */
export const watchDrops = (
  gestures: Pick<Gestures, 'state' | 'onState'> & Partial<Pick<Gestures, 'local'>>, doc: Pick<Document, 'addEventListener' | 'removeEventListener'>,
  accept: (over: Target, held: Held) => boolean, onDrop: (held: Held, over: Target) => void,
  /** What the drop does, in words: the target then glows, counts and is announced as taking the thing (Gestures.local). */
  say?: (held: Held, over: Target) => string | null,
): Unsub => {
  const offLocal = say && gestures.local ? gestures.local((held, over) => (accept(over, held) ? say(held, over) : null)) : null;
  let prev: CarryState = gestures.state();
  let escaped = false;
  const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') escaped = true; };
  doc.addEventListener('keydown', onKey, true);
  const off = gestures.onState((next) => {
    const was = prev;
    prev = next;
    if (next.phase !== 'idle') {
      if (was.phase === 'idle') escaped = false;
      return;
    }
    const cancelled = escaped;
    escaped = false;
    if (clickDropping > 0 || droppedByClick.has(was)) return;
    if (was.phase === 'idle' || was.phase === 'ring' || !was.held || !was.over || cancelled) return;
    if (accept(was.over, was.held)) onDrop(was.held, was.over);
  });
  return () => {
    off();
    offLocal?.();
    doc.removeEventListener('keydown', onKey, true);
  };
};

/** Marks an element as a place a click can drop onto, so only the innermost one answers a click. */
export const DROP_ATTR = 'data-pg-drop';

/**
 * The click path onto a no-verb target: while something is carried, a click drops it here, and
 * only here. The innermost drop place under the click answers it (a gate inside a section), and
 * only when `accept` says this panel does something with what is carried; anything else goes on
 * untouched, to the hand or to the control that was clicked.
 */
export const clickDrop = (
  el: HTMLElement, gestures: Pick<Gestures, 'state' | 'cancel'>, accept: (held: Held) => boolean, onDrop: (held: Held) => void,
): Unsub => {
  el.setAttribute(DROP_ATTR, '');
  const click = (e: MouseEvent): void => {
    const s = gestures.state();
    if (!s.held || (s.phase !== 'carrying' && s.phase !== 'keyboard')) return;
    const at = e.target as { closest?: (selector: string) => Element | null } | null;
    const inner = typeof at?.closest === 'function' ? at.closest(`[${DROP_ATTR}]`) : null;
    if (inner !== null && inner !== el) return;
    if (!accept(s.held)) return;
    e.stopPropagation();
    e.preventDefault();
    const held = s.held;
    droppedByClick.add(s);
    clickDropping += 1;
    try {
      gestures.cancel();
    } finally {
      clickDropping -= 1;
    }
    onDrop(held);
  };
  el.addEventListener('click', click, true);
  return () => {
    el.removeEventListener('click', click, true);
    el.removeAttribute(DROP_ATTR);
  };
};

/** A carry is under way (lifted by pointer, click or keyboard): a target's own keys stay out of the hand's way. */
export const carrying = (gestures: Pick<Gestures, 'state'>): boolean => {
  try {
    return gestures.state().phase !== 'idle';
  } catch {
    return false;
  }
};

const focusKey = (el: HTMLElement): string =>
  `${el.tagName}|${el.getAttribute('aria-label') ?? ''}|${el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' ? (el.getAttribute('placeholder') ?? '') : (el.textContent ?? '')}`;

/** Redraw without dropping keyboard focus: the element that had it is found again by its tag, label and text. */
export const keepFocus = (scope: HTMLElement, redraw: () => void): void => {
  const was = scope.ownerDocument.activeElement;
  const key = was instanceof HTMLElement && scope.contains(was) ? focusKey(was) : null;
  redraw();
  if (key === null || !(was instanceof HTMLElement) || scope.contains(was)) return;
  [...scope.querySelectorAll<HTMLElement>(was.tagName)].find((el) => focusKey(el) === key)?.focus({ preventScroll: true });
};

/** What a hand's verb may add beyond the contract: which held things and targets of its kinds it takes. */
type Fitting = Verb & { readonly heldIs?: (h: Held) => boolean; readonly targetIs?: (t: Target) => boolean };

/**
 * Does a verb already handle this drop? Then the Carry machine fires it and the panel stays out
 * of the way. The kinds must meet, and so must the verb's own narrowing where it has one (a new
 * bridge and not a given one; the antenna rack and not the relay rack).
 */
export const verbCovers = (gestures: Pick<Gestures, 'verbs'>, held: Held, over: Target): boolean => {
  try {
    return gestures.verbs().some((v) => {
      if (v.held !== held.kind || v.target !== over.kind) return false;
      const f = v as Fitting;
      return (typeof f.heldIs === 'function' ? f.heldIs(held) : true) && (typeof f.targetIs === 'function' ? f.targetIs(over) : true);
    });
  } catch {
    return false;
  }
};
